import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CalendarDays, Users, Sparkles, Receipt, Settings as SettingsIcon, UserCog, Gift, BedDouble,
  Car, CarFront, Image as ImageIcon, LogOut, Menu, ExternalLink, UserCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Lotus } from '@/components/ui/Lotus';
import { useAuth, can } from '@/lib/auth';

type NavKey = 'dashboard' | 'bookings' | 'clients' | 'treatments' | 'invoices' | 'giftcards' | 'stays' | 'carhire' | 'fleet' | 'images' | 'users' | 'account' | 'settings';
interface NavItem { to: string; key: NavKey; label: string; icon: LucideIcon; end?: boolean; }
const NAV: NavItem[] = [
  { to: '/admin', key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/bookings', key: 'bookings', label: 'Bookings', icon: CalendarDays },
  { to: '/admin/clients', key: 'clients', label: 'Clients', icon: Users },
  { to: '/admin/treatments', key: 'treatments', label: 'Treatments', icon: Sparkles },
  { to: '/admin/invoices', key: 'invoices', label: 'Invoices & Receipts', icon: Receipt },
  { to: '/admin/giftcards', key: 'giftcards', label: 'Gift Cards', icon: Gift },
  { to: '/admin/stays', key: 'stays', label: 'Stays & Availability', icon: BedDouble },
  { to: '/admin/carhire', key: 'carhire', label: 'Car Hire', icon: CarFront },
  { to: '/admin/fleet', key: 'fleet', label: 'Fleet', icon: Car },
  { to: '/admin/images', key: 'images', label: 'Images', icon: ImageIcon },
  { to: '/admin/users', key: 'users', label: 'User Management', icon: UserCog },
  { to: '/admin/account', key: 'account', label: 'My Account', icon: UserCircle },
  { to: '/admin/settings', key: 'settings', label: 'Settings', icon: SettingsIcon },
];

export function AdminLayout() {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  async function handleSignOut() {
    await signOut();
    navigate('/admin-login');
  }

  return (
    <div className="flex min-h-screen bg-sand">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-espresso-deep text-cream transition-transform lg:static lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2.5 border-b border-cream/10 px-6 py-5">
          <Lotus size={30} className="text-gold" />
          <div>
            <p className="wordmark text-2xl leading-none text-gold">Grace Haven</p>
            <p className="font-body text-[9px] uppercase tracking-[0.4em] text-cream/60">Staff Portal</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-4">
          {NAV.filter((n) => can(role, n.key)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-none px-3 py-2.5 font-body text-sm transition ${
                  isActive ? 'bg-gold/15 text-gold' : 'text-cream/75 hover:bg-cream/5 hover:text-gold'
                }`
              }
            >
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto absolute bottom-0 w-full border-t border-cream/10 p-4">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 font-body text-xs text-cream/60 hover:text-gold">
            <ExternalLink size={14} /> View website
          </Link>
          <button onClick={handleSignOut} className="flex w-full items-center gap-2 px-3 py-2 font-body text-xs text-cream/60 hover:text-blush">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-line bg-paper px-6 py-4">
          <button className="text-espresso lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu size={22} />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <span className="font-body text-sm text-muted">{user?.email}</span>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 font-body text-sm font-semibold text-gold-deep">
              {(user?.email || '?').charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
